import { EVENT_TYPES } from "../midi";

const PIANO_KEYS_COUNT = 88;
const FIRST_KEY_NOTE_NUMBER = 21;

// Define the pattern of white and black keys in an octave
// const KEY_PATTERN = [
//   { type: "white", note: "C" },
//   { type: "black", note: "C#" },
//   { type: "white", note: "D" },
//   { type: "black", note: "D#" },
//   { type: "white", note: "E" },
//   { type: "white", note: "F" },
//   { type: "black", note: "F#" },
//   { type: "white", note: "G" },
//   { type: "black", note: "G#" },
//   { type: "white", note: "A" },
//   { type: "black", note: "A#" },
//   { type: "white", note: "B" },
// ];

export class FancyPiano {
  constructor(container) {
    if (!container) {
      throw new Error("Piano container element is required");
    }
    this.container = container;
    this.keys = new Array(PIANO_KEYS_COUNT);
    this.isSustainPedalPressed = false;
    this.setKey = this.setKey.bind(this);
    this.noteColors = {}; // Store colors for each note
    
    console.log("🎹 FancyPiano initialized with container:", container);
    this.testKeyAccess(); // Test if we can access keys
  }

  testKeyAccess() {
    // Test if we can find piano keys in the container
    const testKey = this.container.querySelector('#key-60'); // Middle C
    if (testKey) {
      console.log("✅ Piano keys accessible. Test key found:", testKey);
    } else {
      console.warn("⚠️ Piano keys not found. Available elements:");
      const allElements = this.container.querySelectorAll('[id^="key-"]');
      console.log("Found elements with key- prefix:", allElements.length);
      if (allElements.length > 0) {
        console.log("Sample key IDs:", Array.from(allElements).slice(0, 5).map(el => el.id));
      }
    }
  }

  getNoteColor(noteNumber) {
    // Generate a consistent color based on the note number
    const hue = ((noteNumber - FIRST_KEY_NOTE_NUMBER) * 360) / PIANO_KEYS_COUNT;
    return `hsl(${hue}, 70%, 50%)`; // HSL color with consistent saturation and lightness
  }

  repaintKeys() {
    console.log("🎨 Repainting all piano keys to default state");
    for (let i = 0; i < PIANO_KEYS_COUNT; i++) {
      const noteNumber = FIRST_KEY_NOTE_NUMBER + i;
      this.paintReleasedKey(noteNumber);
    }
  }

  paintPressedKey(noteNumber) {
    const keyElement = this.container.querySelector(`#key-${noteNumber}`);
    if (keyElement) {
      // Single highlight color for all pressed keys
      keyElement.style.fill = '#4a90e2';
      keyElement.style.stroke = '#1a5fa8';
      keyElement.style.strokeWidth = '1.5';
      keyElement.classList.add('pressed');
    }
  }
  
  paintReleasedKey(noteNumber) {
    const keyElement = this.container.querySelector(`#key-${noteNumber}`);
    if (keyElement) {
      // Reset to default appearance
      keyElement.style.fill = "";
      keyElement.style.stroke = "";
      keyElement.style.strokeWidth = "";
      keyElement.classList.remove("pressed");
      
      // Remove stored color
      delete this.noteColors[noteNumber];
    } else {
      console.warn(`❌ Key element for noteNumber ${noteNumber} not found for release`);
    }
  }
  
  setKey(number, event) {
    this.keys[number] = event;
    this.paintPressedKey(number);
  }

  clearPressedKey(key) {
    this.paintReleasedKey(key);
  }

  setSustainPedal(intensity) {
    this.isSustainPedalPressed = intensity > 32;
    console.log(`🦶 Sustain pedal ${this.isSustainPedalPressed ? 'pressed' : 'released'}`);
    
    if (!intensity) {
      this.getSustainedKeys().forEach((sustainedKey) => {
        if (sustainedKey && sustainedKey.stop) {
          sustainedKey.stop();
        }
      });
    }
  }

  getSustainedKeys() {
    // Return all currently playing keys when sustain pedal is pressed
    return this.keys.filter((keyEvent) => keyEvent && keyEvent !== null);
  }

  stopKey(number) {
    this.paintReleasedKey(number);
    return this.isSustainPedalPressed ? null : this.keys[number];
  }

  handle(event = {}) {
    const { type, context } = event;
    
    if (type === EVENT_TYPES.SUSTAIN_PEDAL_PRESSED) {
      this.setSustainPedal(128); // Full intensity
    } else if (type === EVENT_TYPES.SUSTAIN_PEDAL_RELEASED) {
      this.setSustainPedal(0);
    } else if (type === EVENT_TYPES.KEY_PRESSED) {
      const noteNumber = context?.noteNumber;
      if (noteNumber !== undefined) {
        this.setKey(noteNumber, context.keyEvent);
      } else {
        console.warn("KEY_PRESSED event missing noteNumber:", context);
      }
    } else if (type === EVENT_TYPES.KEY_RELEASED) {
      const noteNumber = context?.noteNumber;
      if (noteNumber !== undefined) {
        // For visual feedback, always release the key immediately
        // even if the audio is sustained
        this.paintReleasedKey(noteNumber);
        
        // If sustain pedal is not pressed, actually stop the key
        if (!this.isSustainPedalPressed && !context?.sustained) {
          this.stopKey(noteNumber);
        }
      } else {
        console.warn("KEY_RELEASED event missing noteNumber:", context);
      }
    }
  }
}
